import React from 'react';
import { motion } from 'framer-motion';
import { Gamepad2, Utensils, Building, Calendar, Shield } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const ICONS = [Gamepad2, Utensils, Building, Calendar, Shield];
const COLORS = [
  { bg: 'rgba(212,175,55,0.08)', border: 'rgba(212,175,55,0.2)', text: '#D4AF37' },
  { bg: 'rgba(107,78,255,0.08)', border: 'rgba(107,78,255,0.2)', text: '#9B7FFF' },
  { bg: 'rgba(212,175,55,0.08)', border: 'rgba(212,175,55,0.2)', text: '#D4AF37' },
  { bg: 'rgba(107,78,255,0.08)', border: 'rgba(107,78,255,0.2)', text: '#9B7FFF' },
  { bg: 'rgba(212,175,55,0.08)', border: 'rgba(212,175,55,0.2)', text: '#D4AF37' },
];

export default function FormatSpace() {
  const { t } = useLang();
  const f = t.format;

  return (
    <section id="format" className="py-32 md:py-40 relative overflow-hidden" style={{ background: '#0A0B0F' }}>
      {/* Background image with overlay */}
      <div className="section-bg">
        <img src="/img/7P3A6815.jpg" alt="" />
      </div>

      <div
        className="orb-gold absolute"
        style={{ width: 700, height: 700, top: '-10%', right: '-5%', opacity: 0.12 }}
      />
      <div
        className="orb-purple absolute"
        style={{ width: 600, height: 600, bottom: '-10%', left: '-8%', opacity: 0.1 }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-20 max-w-4xl"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-bold">Format</span>
          </div>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 font-heading leading-[0.95]" style={{ fontFamily: "'Sora', sans-serif", textShadow: '0 2px 30px rgba(0,0,0,0.5)' }}>
            {f.title}
          </h2>
          <p className="text-[#BFC3C9] text-xl md:text-2xl">{f.subtitle}</p>
        </motion.div>

        {/* Premium Items */}
        <div className="space-y-5">
          {f.items.map((item, i) => {
            const Icon = ICONS[i];
            const color = COLORS[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="glass-premium rounded-3xl p-8 flex items-center gap-8 group card-premium"
                data-testid={`format-item-${i}`}
              >
                {/* Number */}
                <span
                  className="text-6xl font-black opacity-15 font-heading flex-shrink-0 hidden md:block"
                  style={{ color: color.text, lineHeight: 1, width: 80 }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>

                {/* Icon */}
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                  style={{ 
                    background: `linear-gradient(135deg, ${color.bg.replace('0.08', '0.15')} 0%, ${color.bg} 100%)`, 
                    border: `1px solid ${color.border.replace('0.2', '0.3')}`,
                    boxShadow: `0 8px 24px ${color.bg}`
                  }}
                >
                  <Icon size={28} style={{ color: color.text }} />
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-xl md:text-2xl mb-2 font-heading">{item.title}</h3>
                  <p className="text-[#9CA3AF] text-base">{item.desc}</p>
                </div>

                {/* Dot */}
                <div className="format-dot flex-shrink-0 hidden sm:block" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
